import { resolveUploadUrl } from '@/lib/assets/resolveUploadUrl';
import { getBootstrapSplashSnapshot } from '@/lib/branding/bootstrapPublicBranding';
import { DEFAULT_BRAND_TITLE } from '@/lib/branding/loadingBlockConstants';
import { getPublicUiBootstrap } from '@/lib/branding/publicUiBootstrap';
import type { BrandingNormalized, PublicLoadingConfig } from '@/lib/branding/types';
import type { BrandingSettings, UISettings } from '@/contexts/SiteSettingsContext';
import type { LoadingVisualAnimationType } from '@/components/ui/loading-visual';

export type { LoadingBlockConfig } from '@/lib/branding/loadingBlockConstants';

export type BrandAwareLoadingScope = 'fullscreen' | 'content' | 'inline' | 'boot';

export type BrandAssetSource = 'loading_image' | 'logo' | 'favicon' | 'none';

const VALID_LOADING_TYPES: LoadingVisualAnimationType[] = ['default', 'minimal', 'pulse', 'dots'];

export interface BrandAwareLoadingResolved {
  title: string | null;
  brandAssetUrl: string | null;
  brandAssetSource: BrandAssetSource;
  animationType: LoadingVisualAnimationType;
  durationMs: number;
  animationsEnabled: boolean;
  glassEnabled: boolean;
  showTitle: boolean;
  showBrandAsset: boolean;
  configSource: 'site_settings' | 'public_bootstrap' | 'fallback' | 'draft';
}

function sanitizeAnimationType(value: unknown): LoadingVisualAnimationType {
  if (typeof value === 'string' && VALID_LOADING_TYPES.includes(value as LoadingVisualAnimationType)) {
    return value as LoadingVisualAnimationType;
  }
  return 'default';
}

function sanitizeDurationMs(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 1000;
  if (value < 0) return 0;
  if (value > 60000) return 60000;
  return Math.round(value);
}

function resolveBrandAsset(
  loadingUrl: string | null | undefined,
  logoUrl: string | null | undefined,
  faviconUrl: string | null | undefined,
  scope: BrandAwareLoadingScope,
): { url: string | null; source: BrandAssetSource } {
  const toUrl = (raw: string | null | undefined) => {
    if (!raw) return null;
    return resolveUploadUrl(raw);
  };

  const loading = toUrl(loadingUrl);
  if (loading) return { url: loading, source: 'loading_image' };

  const logo = toUrl(logoUrl);
  if (logo) return { url: logo, source: 'logo' };

  if (scope === 'inline') {
    const favicon = toUrl(faviconUrl);
    if (favicon) return { url: favicon, source: 'favicon' };
  }

  return { url: null, source: 'none' };
}

function scopeBehavior(scope: BrandAwareLoadingScope): { showTitle: boolean; showBrandAsset: boolean } {
  if (scope === 'inline') {
    return { showTitle: false, showBrandAsset: false };
  }
  return { showTitle: true, showBrandAsset: true };
}

function resolveDisplayTitle(rawTitle: string | null, scope: BrandAwareLoadingScope): string | null {
  if (rawTitle) return rawTitle;
  if (scope === 'inline') return null;
  return DEFAULT_BRAND_TITLE;
}

function mergeBranding(
  base: BrandingSettings | null | undefined,
  draft: Partial<BrandingSettings> | null | undefined,
  normalized: BrandingNormalized | null,
) {
  return {
    site_title: draft?.site_title ?? base?.site_title,
    loading_image_url: draft?.loading_image_url ?? base?.loading_image_url,
    logo_url: draft?.logo_url ?? base?.logo_url,
    favicon_url: draft?.favicon_url ?? base?.favicon_url,
  };
}

function mergeUi(
  base: UISettings | null | undefined,
  draft: Partial<UISettings> | null | undefined,
  bootstrapUi: PublicLoadingConfig,
): UISettings {
  return {
    enable_animations: draft?.enable_animations ?? base?.enable_animations ?? bootstrapUi.enable_animations ?? true,
    enable_glass_effect: draft?.enable_glass_effect ?? base?.enable_glass_effect ?? true,
    loading_animation_type:
      draft?.loading_animation_type ?? base?.loading_animation_type ?? bootstrapUi.loading_animation_type,
    loader_indicator_duration_ms:
      draft?.loader_indicator_duration_ms ?? base?.loader_indicator_duration_ms ?? bootstrapUi.loader_indicator_duration_ms,
  };
}

export interface ResolveBrandAwareLoadingInput {
  scope: BrandAwareLoadingScope;
  siteBranding?: BrandingSettings | null;
  siteUi?: UISettings | null;
  publicBranding?: BrandingNormalized | null;
  publicUi?: PublicLoadingConfig | null;
  draftBranding?: Partial<BrandingSettings> | null;
  draftUi?: Partial<UISettings> | null;
  useDraft?: boolean;
  animationOverride?: LoadingVisualAnimationType;
  durationOverride?: number;
  animationsOverride?: boolean;
}

export function resolveBrandAwareLoading({
  scope,
  siteBranding,
  siteUi,
  publicBranding,
  publicUi,
  draftBranding,
  draftUi,
  useDraft = false,
  animationOverride,
  durationOverride,
  animationsOverride,
}: ResolveBrandAwareLoadingInput): BrandAwareLoadingResolved {
  const bootstrap = getBootstrapSplashSnapshot();
  const bootstrapUi = publicUi ?? getPublicUiBootstrap() ?? bootstrap.publicUi;
  const normalized = publicBranding ?? bootstrap.branding;

  const branding = mergeBranding(siteBranding, draftBranding, normalized);
  const ui = mergeUi(siteUi, draftUi, bootstrapUi);

  const rawTitle = branding.site_title?.trim() || normalized.site_title?.trim() || null;
  const title = resolveDisplayTitle(rawTitle, scope);

  const loadingRaw = branding.loading_image_url ?? normalized.loading_src;
  const logoRaw = branding.logo_url ?? normalized.logo_src;
  const faviconRaw = branding.favicon_url ?? normalized.favicon_src;

  const { url: brandAssetUrl, source: brandAssetSource } = resolveBrandAsset(
    loadingRaw,
    logoRaw,
    faviconRaw,
    scope,
  );

  const animationType = animationOverride ?? sanitizeAnimationType(ui.loading_animation_type);
  const durationMs = durationOverride ?? sanitizeDurationMs(ui.loader_indicator_duration_ms);
  const animationsEnabled = animationsOverride ?? ui.enable_animations ?? true;
  const glassEnabled = ui.enable_glass_effect ?? true;

  const hasDraft = useDraft || !!draftBranding || !!draftUi;
  const configSource: BrandAwareLoadingResolved['configSource'] = hasDraft
    ? 'draft'
    : siteUi
      ? 'site_settings'
      : bootstrapUi
        ? 'public_bootstrap'
        : 'fallback';

  const { showTitle, showBrandAsset } = scopeBehavior(scope);

  return {
    title,
    brandAssetUrl,
    brandAssetSource,
    animationType,
    durationMs,
    animationsEnabled,
    glassEnabled,
    showTitle,
    showBrandAsset,
    configSource,
  };
}

export const buildLoadingBlockConfig = resolveBrandAwareLoading;
